import {
  type BorderStyle,
  type Colors,
  type ThemeColors,
} from '@factorialco/deprecated-design-system'
import { type Edge as ReactFlowEdge } from '@xyflow/react'

import { type CustomNode } from './types'

/**
 * Builds a map of parent node IDs to their children node IDs from edges
 */
export const buildChildrenMap = (edges: ReactFlowEdge[]): Map<string, string[]> => {
  const childrenMap = new Map<string, string[]>()

  edges.forEach((edge) => {
    const children = childrenMap.get(edge.source) || []
    children.push(edge.target)
    childrenMap.set(edge.source, children)
  })

  return childrenMap
}

/**
 * Builds a map of child node IDs to their parent node ID from edges
 */
export const buildParentMap = (edges: ReactFlowEdge[]): Map<string, string> => {
  const parentMap = new Map<string, string>()

  edges.forEach((edge) => {
    parentMap.set(edge.target, edge.source)
  })

  return parentMap
}

/**
 * Builds a map of node IDs to their node objects for efficient lookups
 */
export const buildNodeMap = (nodes: CustomNode[]): Map<string, CustomNode> => {
  const nodeMap = new Map<string, CustomNode>()

  nodes.forEach((node) => {
    nodeMap.set(node.id, node)
  })

  return nodeMap
}

/**
 * Finds all descendants of a node using BFS traversal
 */
export const getDescendants = (nodeId: string, childrenMap: Map<string, string[]>): string[] => {
  const descendants: string[] = []
  const queue = [nodeId]

  while (queue.length > 0) {
    const current = queue.shift()
    if (!current) continue

    const children = childrenMap.get(current) || []
    descendants.push(...children)
    queue.push(...children)
  }

  return descendants
}

/**
 * Finds all ancestors of a node by traversing up the tree
 */
export const getAncestors = (nodeId: string, parentMap: Map<string, string>): string[] => {
  const ancestors: string[] = []
  let currentId: string | undefined = nodeId

  while (currentId) {
    const parentId = parentMap.get(currentId)
    if (parentId) {
      ancestors.push(parentId)
      currentId = parentId
    } else {
      break
    }
  }

  return ancestors
}

/**
 * Finds the parent node of a node
 */
export const getParentNode = (
  nodeId: string,
  edges: ReactFlowEdge[],
  nodes: CustomNode[]
): CustomNode | undefined => {
  const parentMap = buildParentMap(edges)
  const nodeMap = buildNodeMap(nodes)
  const parentId = parentMap.get(nodeId)

  return parentId ? nodeMap.get(parentId) : undefined
}

/**
 * Finds the sibling node IDs of a node (nodes with the same parent)
 */
const getSiblings = (
  nodeId: string,
  parentMap: Map<string, string>,
  childrenMap: Map<string, string[]>
): string[] => {
  const parentId = parentMap.get(nodeId)
  if (!parentId) return []

  const siblings = childrenMap.get(parentId) || []
  // Exclude the node itself from the siblings list
  return siblings.filter((id) => id !== nodeId)
}

export const getNodeBorderColor = (
  isSelected: boolean,
  isChecked: boolean
): BorderStyle['color'] => {
  if (isSelected) {
    return 'viridian800'
  }
  if (isChecked) {
    return 'viridian400'
  }

  return 'grey400'
}

export const getNodeBackgroundColor = (isChecked: boolean): Colors | ThemeColors => {
  if (isChecked) {
    return 'viridian100'
  }

  return 'containerBackground'
}
